import {
  computed,
  effect,
  inject,
  Injectable,
  resource,
  ResourceLoaderParams,
  ResourceRef,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { CONFIGURATION_SETTING_KEYS, DEFAULT_TICKET, ITicket } from '@web/dtos';
import { IApiResponse } from '@web/shared-types';
import { firstValueFrom } from 'rxjs';
import { ConfigurationStore } from '../configuration/configuration-store.service';
import { TicketGateway } from './ticket.gateway';

interface ITicketRequest {
  serviceAreaId: number;
}

@Injectable({
  providedIn: 'root',
})
export class TicketStore {
  private readonly gateway: TicketGateway = new TicketGateway();
  private readonly configurationStore: ConfigurationStore =
    inject(ConfigurationStore);
  private readonly serviceAreaId = computed(() =>
    this.configurationStore.getNumberConfiguration(
      CONFIGURATION_SETTING_KEYS.ServiceAreaId
    )
  );

  private readonly ticketRequest: Signal<ITicketRequest> = computed(
    (): ITicketRequest => ({
      serviceAreaId: this.serviceAreaId(),
    })
  );

  readonly #ticketResource: ResourceRef<IApiResponse<ITicket[]> | undefined> =
    resource({
      params: this.ticketRequest,
      loader: ({ params }: ResourceLoaderParams<ITicketRequest>) => {
        return params
          ? firstValueFrom(this.gateway.list(params.serviceAreaId))
          : Promise.resolve(undefined);
      },
    });

  readonly isLoading: Signal<boolean> = computed((): boolean =>
    this.#ticketResource.isLoading()
  );
  readonly wasSaved: WritableSignal<boolean> = signal<boolean>(false);
  readonly tickets: Signal<ITicket[]> = computed((): ITicket[] =>
    this.#ticketResource.hasValue()
      ? (this.#ticketResource.value()?.data ?? [DEFAULT_TICKET])
      : [DEFAULT_TICKET]
  );

  constructor() {
    effect((): void => {
      if (this.wasSaved()) {
        setTimeout((): void => this.wasSaved.set(false), 0);
      }
    });
  }

  reloadTickets(): void {
    this.#ticketResource.reload();
  }

  save(query: ITicket): void {
    firstValueFrom(this.gateway.save(this.withServiceAreaId(query))).then(
      (): void => {
        this.wasSaved.set(true);
        this.reloadTickets();
      }
    );
  }

  async saveAll(query: ITicket[]): Promise<void> {
    await firstValueFrom(
      this.gateway.saveAll(query.map(ticket => this.withServiceAreaId(ticket)))
    );
    this.reloadTickets();
  }

  private withServiceAreaId(ticket: ITicket): ITicket {
    return {
      ...ticket,
      serviceAreaId:
        ticket.serviceAreaId ??
        this.configurationStore.getNumberConfiguration(
          CONFIGURATION_SETTING_KEYS.ServiceAreaId
        ),
    };
  }
}
